import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Alert,
} from "@mui/material";
import { Business } from "@mui/icons-material";

function SponsorAdd({
  onClose,
  onSave,
  availableTiers = [],
  editingSponsor = null,
  isEditMode = false,
}) {
  const [formData, setFormData] = useState({
    name: "",
    website: "",
    tier: "",
    logo: "",
  });

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Load data when editing
  useEffect(() => {
    console.log("🏷️ SponsorAdd: availableTiers:", availableTiers);
    console.log("🏷️ SponsorAdd: isEditMode:", isEditMode);
    console.log("🏷️ SponsorAdd: editingSponsor:", editingSponsor);

    if (isEditMode && editingSponsor) {
      setFormData({
        name: editingSponsor.name || "",
        website: editingSponsor.website || "",
        tier: editingSponsor.tier || "",
        logo: editingSponsor.logo || "",
      });
    } else {
      // Reset for new sponsor
      setFormData({
        name: "",
        website: "",
        tier: "",
        logo: "",
      });
    }
  }, [isEditMode, editingSponsor, availableTiers]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      setError("File size must be less than 1MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData((prev) => ({
        ...prev,
        logo: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setError("");

    // Validation
    if (!formData.name.trim()) {
      setError("Please enter a sponsor name");
      return;
    }
    if (!formData.tier) {
      setError("Please select a sponsor tier");
      return;
    }

    setSaving(true);

    try {
      const sponsorData = {
        name: formData.name.trim(),
        website: formData.website.trim(),
        tier: formData.tier,
        logo: formData.logo,
      };

      await onSave(sponsorData);

      // Close modal immediately after save
      onClose();
    } catch (error) {
      console.error("Error saving sponsor:", error);
      setError("Failed to save sponsor. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: 450, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? "Edit Sponsor" : "Add New Sponsor"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Logo Section */}
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Avatar
            src={formData.logo}
            sx={{
              width: 120,
              height: 120,
              mx: "auto",
              mb: 2,
              backgroundColor: formData.logo ? "transparent" : "primary.main",
            }}
          >
            {!formData.logo && <Business sx={{ fontSize: "3rem" }} />}
          </Avatar>

          <input
            accept="image/*"
            style={{ display: "none" }}
            id="logo-upload"
            type="file"
            onChange={handleLogoUpload}
          />
          <label htmlFor="logo-upload">
            <Button variant="contained" component="span">
              Upload Logo
            </Button>
          </label>

          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Max 1MB | JPG, PNG, SVG
          </Typography>
        </Grid>

        {/* Name Field */}
        <Grid item xs={12}>
          <TextField
            label="Sponsor Name *"
            fullWidth
            value={formData.name}
            onChange={handleChange("name")}
            placeholder="Enter sponsor name"
          />
        </Grid>

        {/* Website Field */}
        <Grid item xs={12}>
          <TextField
            label="Website URL"
            fullWidth
            value={formData.website}
            onChange={handleChange("website")}
            placeholder="https://example.com"
          />
        </Grid>

        {/* Tier Field */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Sponsor Tier *</InputLabel>
            <Select
              value={formData.tier}
              label="Sponsor Tier *"
              onChange={handleChange("tier")}
            >
              {availableTiers && availableTiers.length > 0 ? (
                availableTiers.map((tier, index) => (
                  <MenuItem key={tier.id || index} value={tier.name}>
                    {index + 1}. {tier.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  {availableTiers === undefined
                    ? "Loading tiers..."
                    : "No tiers available - Please create tiers first"}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <DialogActions sx={{ mt: 3, px: 0 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? "Saving..." : isEditMode ? "Update Sponsor" : "Add Sponsor"}
        </Button>
      </DialogActions>
    </Box>
  );
}

export default SponsorAdd;
